import { useRef } from "react";
import Reveal from "@/components/Reveal";

const points = [
  ["Detect", "Hesitation, objections and changes in buyer intent"],
  ["Ground", "The right product, pricing or policy detail"],
  ["Guide", "A concise response or next action for the rep"],
];

const repWave = [
  [12, "-.1s"],
  [25, "-.4s"],
  [17, "-.7s"],
  [29, "-.2s"],
  [20, "-.8s"],
  [10, "-.5s"],
  [23, "-.3s"],
  [15, "-.9s"],
];
const buyerWave = [
  [16, "-.6s"],
  [27, "-.2s"],
  [12, "-.8s"],
  [31, "-.4s"],
  [21, "-.1s"],
  [14, "-.7s"],
  [25, "-.3s"],
  [18, "-.9s"],
];

const Wave = ({ bars }) => (
  <div className="wave">
    {bars.map(([h, d], i) => (
      <i key={i} style={{ "--h": `${h}px`, "--d": d }} />
    ))}
  </div>
);

export default function Perform() {
  const assetRef = useRef(null);

  const reset = () => {
    const el = assetRef.current;
    if (!el) return;
    const vars = {
      "--tilt-x": "0deg",
      "--tilt-y": "0deg",
      "--shift-x": "0px",
      "--shift-y": "0px",
      "--shift-x2": "0px",
      "--shift-y2": "0px",
      "--spot-x": "50%",
      "--spot-y": "50%",
    };
    Object.entries(vars).forEach(([k, v]) => el.style.setProperty(k, v));
  };

  const onMove = (e) => {
    const el = assetRef.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const r = el.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 2;
    const y = ((e.clientY - r.top) / r.height - 0.5) * 2;
    el.style.setProperty("--tilt-x", `${x * 2.2}deg`);
    el.style.setProperty("--tilt-y", `${-y * 2}deg`);
    el.style.setProperty("--shift-x", `${x * 8}px`);
    el.style.setProperty("--shift-y", `${y * 7}px`);
    el.style.setProperty("--shift-x2", `${-x * 11}px`);
    el.style.setProperty("--shift-y2", `${-y * 9}px`);
    el.style.setProperty("--spot-x", `${(x + 1) * 50}%`);
    el.style.setProperty("--spot-y", `${(y + 1) * 50}%`);
  };

  return (
    <section className="perform" id="perform" data-testid="perform-section">
      <div className="wrap">
        <article className="phase">
          <div className="phase-grid">
            <div>
              <div className="label mono">Perform</div>
              <h3>Live signals become the next best response</h3>
              <p>
                ThinkWire listens privately during the call. It catches objections and intent shifts,
                finds the relevant answer and shapes the reply before momentum drops.
              </p>
              <div className="phase-points">
                {points.map(([k, v]) => (
                  <div className="phase-point" key={k}>
                    <strong>{k}</strong>
                    <span>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            <Reveal
              className="live-coach"
              id="live-coach-asset"
              data-testid="live-coach"
              aria-label="Illustration of ThinkWire detecting a price objection and preparing live guidance"
              ref={assetRef}
              onPointerMove={onMove}
              onPointerLeave={reset}
            >
              <div className="coach-top">
                <span className="coach-brand">THINKWIRE · LIVE INTELLIGENCE</span>
                <span className="coach-live">
                  <i />
                  LIVE · 08:21
                </span>
              </div>
              <div className="coach-stage">
                <div className="coach-channels">
                  <div className="coach-channel">
                    <div className="channel-head">
                      <span>CH · 01 REP</span>
                      <span>44%</span>
                    </div>
                    <Wave bars={repWave} />
                    <div className="channel-copy">
                      The construction-linked plan keeps the early outflow lower.
                    </div>
                  </div>
                  <div className="coach-channel buyer">
                    <div className="channel-head">
                      <span>CH · 02 BUYER</span>
                      <span>56%</span>
                    </div>
                    <Wave bars={buyerWave} />
                    <div className="channel-copy">
                      I still feel the other property is more affordable.
                    </div>
                  </div>
                </div>
                <div className="signal-card objection-card">
                  <small>OBJECTION · PRICE COMPARISON</small>
                  <b>Buyer is weighing booking price over long-term value.</b>
                </div>
                <div className="signal-card guidance-card">
                  <small>THINKWIRE WHISPER · GROUNDED</small>
                  <b>
                    Acknowledge the budget. Reframe using value-per-month and possession certainty.
                  </b>
                </div>
                <div className="signal-card next-line">
                  <small>NEXT BEST LINE</small>
                  <b>“Would comparing the full ownership cost make the trade-off clearer?”</b>
                </div>
              </div>
              <div className="coach-footer">
                <div className="coach-stat">
                  <small>SIGNAL CONFIDENCE</small>
                  <b className="teal">92%</b>
                </div>
                <div className="coach-stat">
                  <small>APPROVED SOURCE</small>
                  <b>Payment plan v3</b>
                </div>
                <div className="coach-stat">
                  <small>NEXT ACTION</small>
                  <b>Propose site visit</b>
                </div>
              </div>
            </Reveal>
          </div>
        </article>
      </div>
    </section>
  );
}
